import React, { useMemo } from "react";
import {
  addDays,
  addMinutes,
  differenceInMinutes,
  endOfDay,
  endOfHour,
  format,
  getHours,
  startOfDay,
  startOfHour,
  startOfWeek,
} from "date-fns";
import { Event } from "../../types/events";
import { SchedulerTheme, ViewConfig, ViewInteractions } from "../../types/views";

const HOUR_HEIGHT = 72;
const SLOT_MINUTES = 30;
const PIXELS_PER_MINUTE = HOUR_HEIGHT / 60;
const MAX_TRUCKING_CALENDAR_DURATION_MINUTES = 60;

interface TimeGridViewProps {
  events: Event[];
  currentDate: Date;
  daysToShow: number;
  startHour?: number;
  endHour?: number;
  onEventClick?: ViewInteractions["onEventClick"];
  onTimeSlotClick?: ViewInteractions["onTimeSlotClick"];
  viewConfig?: ViewConfig;
  schedulerTheme?: SchedulerTheme;
}

const getCalendarDisplayRange = (event: Event) => {
  const rawStart = new Date(event.timeRange.start);
  const rawEnd = new Date(event.timeRange.end);

  // Trucking scheduler data currently uses a smart dispatch/departure start time.
  // In day/week calendar views we want the visual block to reflect the scheduled
  // appointment window instead of the full operational travel span.
  if (event.industryType === "trucking") {
    const tripDurationMinutes = Math.max(
      differenceInMinutes(rawEnd, rawStart),
      SLOT_MINUTES
    );
    const displayDurationMinutes = Math.max(
      Math.min(tripDurationMinutes, MAX_TRUCKING_CALENDAR_DURATION_MINUTES),
      SLOT_MINUTES
    );

    return {
      start: addMinutes(rawEnd, -displayDurationMinutes),
      end: rawEnd,
      labelStart: rawEnd,
      labelEnd: rawEnd,
    };
  }

  return {
    start: rawStart,
    end: rawEnd,
    labelStart: rawStart,
    labelEnd: rawEnd,
  };
};

const createDayLanes = (dayEvents: Event[]) => {
  const sortedEvents = [...dayEvents].sort(
    (left, right) =>
      getCalendarDisplayRange(left).start.getTime() -
      getCalendarDisplayRange(right).start.getTime()
  );
  const laneEndTimes: number[] = [];

  return sortedEvents.map((event) => {
    const displayRange = getCalendarDisplayRange(event);
    const startTime = displayRange.start.getTime();
    const endTime = displayRange.end.getTime();
    let laneIndex = laneEndTimes.findIndex((laneEndTime) => startTime >= laneEndTime);

    if (laneIndex === -1) {
      laneIndex = laneEndTimes.length;
      laneEndTimes.push(endTime);
    } else {
      laneEndTimes[laneIndex] = endTime;
    }

    return {
      event,
      laneIndex,
      laneCount: Math.max(laneEndTimes.length, 1),
    };
  });
};

const TimeGridView: React.FC<TimeGridViewProps> = ({
  events,
  currentDate,
  daysToShow,
  startHour = 6,
  endHour = 20,
  onEventClick,
  onTimeSlotClick,
  viewConfig,
  schedulerTheme,
}) => {
  const dayStart = useMemo(
    () =>
      daysToShow === 1
        ? startOfDay(currentDate)
        : startOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate, daysToShow]
  );

  const days = useMemo(
    () => Array.from({ length: daysToShow }, (_, index) => addDays(dayStart, index)),
    [dayStart, daysToShow]
  );

  const visibleRange = useMemo(() => {
    const visibleEvents = events.filter((event) => {
      const displayRange = getCalendarDisplayRange(event);
      const eventStart = displayRange.start;
      const eventEnd = displayRange.end;

      return (
        eventStart < endOfDay(addDays(dayStart, daysToShow - 1)) &&
        eventEnd > startOfDay(dayStart)
      );
    });

    if (!visibleEvents.length) {
      return { startHour, endHour };
    }

    const earliestVisibleStart = visibleEvents.reduce((earliest, event) => {
      const eventStart = getCalendarDisplayRange(event).start;
      return eventStart < earliest ? eventStart : earliest;
    }, getCalendarDisplayRange(visibleEvents[0]).start);

    const latestVisibleEnd = visibleEvents.reduce((latest, event) => {
      const eventEnd = getCalendarDisplayRange(event).end;
      return eventEnd > latest ? eventEnd : latest;
    }, getCalendarDisplayRange(visibleEvents[0]).end);

    return {
      startHour: Math.min(startHour, getHours(startOfHour(earliestVisibleStart))),
      endHour: Math.max(endHour, getHours(endOfHour(latestVisibleEnd)) + 1),
    };
  }, [dayStart, daysToShow, endHour, events, startHour]);

  const effectiveStartHour = visibleRange.startHour;
  const effectiveEndHour = visibleRange.endHour;
  const visibleMinutes = (effectiveEndHour - effectiveStartHour) * 60;
  const gridHeight = ((effectiveEndHour - effectiveStartHour) * HOUR_HEIGHT) || HOUR_HEIGHT;
  const slotCount = Math.max(1, visibleMinutes / SLOT_MINUTES);
  const slotHeight = SLOT_MINUTES * PIXELS_PER_MINUTE;

  const laneMaps = useMemo(() => {
    const maps = new Map<
      string,
      Array<{ event: Event; laneIndex: number; laneCount: number }>
    >();

    days.forEach((day) => {
      const dayEvents = events.filter((event) => {
        const displayRange = getCalendarDisplayRange(event);
        const eventStart = displayRange.start;
        const eventEnd = displayRange.end;
        return eventStart < endOfDay(day) && eventEnd > startOfDay(day);
      });

      maps.set(format(day, "yyyy-MM-dd"), createDayLanes(dayEvents));
    });

    return maps;
  }, [days, events]);

  return (
    <div className="h-full w-full bg-white overflow-hidden flex flex-col">
      <div className="flex-1 overflow-auto">
        {/* Sticky day strip. Below `sm` we reserve only 56px for the time
            gutter and tighten cell padding so 7-day Week view stays legible
            on tablet portrait (~768px and below). The CSS variable lets the
            same `<style>` block scale across breakpoints without forking the
            grid template. */}
        <div
          className="grid sticky top-0 z-10 border-b border-gray-200 bg-gray-50 [--time-col-w:56px] sm:[--time-col-w:72px] md:[--time-col-w:80px]"
          style={{
            gridTemplateColumns: `var(--time-col-w) repeat(${daysToShow}, minmax(0, 1fr))`,
          }}
        >
          <div className="border-r border-gray-200 px-2 py-2 sm:px-3 sm:py-3 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500">
            Time
          </div>
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className="border-r border-gray-200 px-1.5 py-2 sm:px-3 sm:py-3 text-xs sm:text-sm font-semibold text-gray-700 last:border-r-0 truncate"
            >
              <div className="truncate">{format(day, "EEE")}</div>
              <div className="text-[10px] sm:text-xs font-normal text-gray-500 truncate">
                {format(day, "MMM d")}
              </div>
            </div>
          ))}
        </div>

        <div
          className="grid relative [--time-col-w:56px] sm:[--time-col-w:72px] md:[--time-col-w:80px]"
          style={{
            height: gridHeight,
            minHeight: gridHeight,
            gridTemplateColumns: `var(--time-col-w) repeat(${daysToShow}, minmax(0, 1fr))`,
          }}
        >
          <div className="border-r border-gray-200 bg-gray-50">
            {Array.from({ length: effectiveEndHour - effectiveStartHour }, (_, index) => {
              const hour = effectiveStartHour + index;
              return (
                <div
                  key={hour}
                  className="border-b border-gray-200 px-2 sm:px-3 text-[10px] sm:text-xs text-gray-500"
                  style={{ height: HOUR_HEIGHT }}
                >
                  <div className="pt-2 leading-none">
                    {format(new Date(2000, 0, 1, hour, 0, 0, 0), "h a")}
                  </div>
                </div>
              );
            })}
          </div>

          {days.map((day) => {
            const dayKey = format(day, "yyyy-MM-dd");
            const dayLaneEntries = laneMaps.get(dayKey) || [];
            const dayStartTime = addMinutes(
              startOfDay(day),
              effectiveStartHour * 60
            );
            const dayEndTime = addMinutes(startOfDay(day), effectiveEndHour * 60);

            return (
              <div
                key={dayKey}
                className="relative border-r border-gray-200 last:border-r-0"
                style={{ height: gridHeight, minHeight: gridHeight }}
              >
                {Array.from({ length: slotCount }, (_, slotIndex) => {
                  const slotStart = addMinutes(dayStartTime, slotIndex * SLOT_MINUTES);
                  const slotEnd = addMinutes(slotStart, SLOT_MINUTES);
                  const timeSlot = {
                    start: slotStart,
                    end: slotEnd,
                    isWorkHour: true,
                    isWeekend: slotStart.getDay() === 0 || slotStart.getDay() === 6,
                    events: [],
                    resources: [],
                  };

                  return (
                    <button
                      key={`${dayKey}-${slotIndex}`}
                      type="button"
                      className="absolute inset-x-0 border-b border-gray-100 hover:bg-blue-50/70"
                      style={{
                        top: slotIndex * slotHeight,
                        height: slotHeight,
                        backgroundColor:
                          schedulerTheme?.getGridCellColor?.(timeSlot) || "#ffffff",
                      }}
                      onClick={(event) =>
                        onTimeSlotClick?.(
                          timeSlot,
                          event.currentTarget as unknown as HTMLElement
                        )
                      }
                    />
                  );
                })}

                {Array.from(
                  { length: effectiveEndHour - effectiveStartHour },
                  (_, hourIndex) => (
                  <div
                    key={`${dayKey}-hour-${hourIndex}`}
                    className="absolute inset-x-0 border-b border-gray-200 pointer-events-none"
                    style={{
                      top: hourIndex * HOUR_HEIGHT,
                      height: HOUR_HEIGHT,
                    }}
                  />
                  )
                )}

                {dayLaneEntries.map(({ event, laneIndex, laneCount }) => {
                  const displayRange = getCalendarDisplayRange(event);
                  const eventStart = displayRange.start;
                  const eventEnd = displayRange.end;
                  const displayStart =
                    eventStart > dayStartTime ? eventStart : dayStartTime;
                  const displayEnd = eventEnd < dayEndTime ? eventEnd : dayEndTime;

                  if (displayEnd <= displayStart) {
                    return null;
                  }

                  const topMinutes = differenceInMinutes(displayStart, dayStartTime);
                  const durationMinutes = Math.max(
                    differenceInMinutes(displayEnd, displayStart),
                    SLOT_MINUTES
                  );
                  const top = topMinutes * PIXELS_PER_MINUTE;
                  const eventHeight = Math.max(
                    durationMinutes * PIXELS_PER_MINUTE,
                    slotHeight
                  );
                  const width = `calc(${100 / laneCount}% - 8px)`;
                  const left = `calc(${(laneIndex / laneCount) * 100}% + 4px)`;
                  const eventColor =
                    schedulerTheme?.getEventColor?.(event) || "#2563eb";

                  return (
                    <button
                      key={`${event.id}-${dayKey}-${laneIndex}`}
                      type="button"
                      className="absolute rounded-md px-2 py-1 text-left shadow-sm"
                      style={{
                        top,
                        left,
                        width,
                        height: eventHeight,
                        backgroundColor: eventColor,
                        color: schedulerTheme?.eventTextColor || "#ffffff",
                      }}
                      onClick={(buttonEvent) =>
                        onEventClick?.(
                          event,
                          buttonEvent.currentTarget as unknown as HTMLElement,
                          viewConfig
                        )
                      }
                    >
                      <div className="truncate text-xs font-semibold">{event.title}</div>
                      <div className="truncate text-[11px] opacity-80">
                        {format(displayRange.labelStart, "h:mm a")}
                        {displayRange.labelStart.getTime() !==
                        displayRange.labelEnd.getTime()
                          ? ` - ${format(displayRange.labelEnd, "h:mm a")}`
                          : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeGridView;
